import { ChevronRight, CircleAlert, CornerDownRight } from "lucide-react";
import { Fragment, useState } from "react";
import { formatCellValue } from "@/components/contracts/table-utils";
import {
  FieldLabel,
  MasterDataSelect,
  NumberField,
  NumericMasterDataSelect,
  TextField,
} from "@/components/copy-operations/field-inputs";
import { DataTable } from "@/components/shared/data-table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type {
  ContractDriver,
  ContractFacility,
  ContractIncludedItem,
  ContractMovement,
  ContractMovementData,
  ContractPickup,
  ContractSetdown,
  ContractVehicle,
} from "@/lib/coach-manager/types";
import { cn } from "@/lib/utils";

interface EditableMovementsProps {
  movements: ContractMovement[];
  targetBuId: string;
  onChange: (movements: ContractMovement[]) => void;
  disabled?: boolean;
}

function movementSummary(movement: ContractMovement): string {
  return (
    movement.MovementDescription ||
    [movement.Pickup, movement.Destination].filter(Boolean).join(" → ") ||
    "Movement"
  );
}

function EditableVehicles({
  vehicles,
  targetBuId,
  onChange,
  disabled,
}: {
  vehicles: ContractVehicle[];
  targetBuId: string;
  onChange: (vehicles: ContractVehicle[]) => void;
  disabled?: boolean;
}) {
  if (vehicles.length === 0) return null;

  function updateVehicle(index: number, patch: Partial<ContractVehicle>) {
    const current = vehicles[index];
    if (!current) return;
    const next = vehicles.map((v, i) => (i === index ? Object.assign({}, current, patch) : v));
    onChange(next);
  }

  function updateDriver(vIndex: number, dIndex: number, patch: Partial<ContractDriver>) {
    const vehicle = vehicles[vIndex];
    const existingDrivers = vehicle?.ContractDrivers ?? [];
    const currentDriver = existingDrivers[dIndex];
    if (!currentDriver) return;
    const drivers = existingDrivers.map((d, i) =>
      i === dIndex ? Object.assign({}, currentDriver, patch) : d,
    );
    updateVehicle(vIndex, { ContractDrivers: drivers });
  }

  return (
    <DataTable
      headers={[
        { label: <FieldLabel label="Vehicle Type" mapped /> },
        { label: <FieldLabel label="Seats" required /> },
        { label: <FieldLabel label="Revenue" required /> },
        { label: "Default Vehicle ID" },
      ]}
    >
      {vehicles.map((vehicle, index) => {
        const drivers = vehicle.ContractDrivers ?? [];
        return (
          <Fragment key={index}>
            <tr className="border-b last:border-b-0">
              <td className="px-4 py-2">
                <MasterDataSelect
                  targetBuId={targetBuId}
                  entityType="vehicle_type"
                  value={vehicle.VehicleType}
                  disabled={disabled}
                  className="w-40"
                  onChange={(v) => updateVehicle(index, { VehicleType: v })}
                />
              </td>
              <td className="px-4 py-2">
                <Input
                  type="number"
                  className="h-8 w-20 text-sm"
                  value={vehicle.Seats}
                  disabled={disabled}
                  onChange={(e) => updateVehicle(index, { Seats: Number(e.target.value) || 0 })}
                />
              </td>
              <td className="px-4 py-2">
                <Input
                  type="number"
                  className="h-8 w-24 text-sm"
                  value={vehicle.Revenue}
                  disabled={disabled}
                  onChange={(e) => updateVehicle(index, { Revenue: Number(e.target.value) || 0 })}
                />
              </td>
              <td className="px-4 py-2">
                <Input
                  className="h-8 w-32 text-sm"
                  value={vehicle.DefaultVehicleID ?? ""}
                  disabled={disabled}
                  onChange={(e) => updateVehicle(index, { DefaultVehicleID: e.target.value })}
                />
              </td>
            </tr>
            {drivers.map((driver, dIndex) => (
              <tr
                key={`${index}-driver-${dIndex}`}
                className="border-b bg-muted/20 last:border-b-0"
              >
                <td colSpan={4} className="px-4 py-1.5 pl-10">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Driver:
                      <span title="Transformed to a target-BU-specific value on copy">
                        <CircleAlert
                          className="size-3 shrink-0 text-amber-500"
                          aria-label="Transformed to a target-BU-specific value on copy"
                        />
                      </span>
                    </span>
                    <MasterDataSelect
                      targetBuId={targetBuId}
                      entityType="driver_type"
                      value={driver.DriverType}
                      disabled={disabled}
                      className="w-40"
                      onChange={(v) => updateDriver(index, dIndex, { DriverType: v })}
                    />
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Default Driver ID:
                    </span>
                    <Input
                      className="h-8 w-32 text-sm"
                      value={driver.DefaultDriverID ?? ""}
                      disabled={disabled}
                      onChange={(e) =>
                        updateDriver(index, dIndex, { DefaultDriverID: e.target.value })
                      }
                    />
                  </div>
                </td>
              </tr>
            ))}
          </Fragment>
        );
      })}
    </DataTable>
  );
}

function EditablePickups({
  pickups,
  onChange,
  disabled,
}: {
  pickups: ContractPickup[];
  onChange: (pickups: ContractPickup[]) => void;
  disabled?: boolean;
}) {
  if (pickups.length === 0) return null;
  return (
    <DataTable
      headers={[
        { label: "Pickup Point" },
        { label: <FieldLabel label="Arrival Day" required /> },
        { label: "Arrival Time" },
        { label: <FieldLabel label="Pickup Day" required /> },
        { label: "Pickup Time" },
        { label: "Instructions" },
      ]}
    >
      {pickups.map((pickup, index) => (
        <tr key={index} className="border-b last:border-b-0">
          <td className="px-4 py-2">
            <Input
              className="h-8 w-32 text-sm"
              value={pickup.PickupPoint ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...pickups];
                next[index] = { ...pickup, PickupPoint: e.target.value };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              type="number"
              className="h-8 w-16 text-sm"
              value={pickup.ArrivalDayNo}
              disabled={disabled}
              onChange={(e) => {
                const next = [...pickups];
                next[index] = { ...pickup, ArrivalDayNo: Number(e.target.value) || 0 };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              className="h-8 w-24 text-sm"
              value={pickup.ArrivalTime ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...pickups];
                next[index] = { ...pickup, ArrivalTime: e.target.value };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              type="number"
              className="h-8 w-16 text-sm"
              value={pickup.PickupDayNo}
              disabled={disabled}
              onChange={(e) => {
                const next = [...pickups];
                next[index] = { ...pickup, PickupDayNo: Number(e.target.value) || 0 };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              className="h-8 w-24 text-sm"
              value={pickup.PickupTime ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...pickups];
                next[index] = { ...pickup, PickupTime: e.target.value };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              className="h-8 w-40 text-sm"
              value={pickup.Instructions ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...pickups];
                next[index] = { ...pickup, Instructions: e.target.value };
                onChange(next);
              }}
            />
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

function EditableSetdowns({
  setdowns,
  onChange,
  disabled,
}: {
  setdowns: ContractSetdown[];
  onChange: (setdowns: ContractSetdown[]) => void;
  disabled?: boolean;
}) {
  if (setdowns.length === 0) return null;
  return (
    <DataTable
      headers={[
        { label: "Setdown Point" },
        { label: <FieldLabel label="Arrival Day" required /> },
        { label: "Arrival Time" },
        { label: <FieldLabel label="Setdown Day" required /> },
        { label: "Setdown Time" },
        { label: "Instructions" },
      ]}
    >
      {setdowns.map((setdown, index) => (
        <tr key={index} className="border-b last:border-b-0">
          <td className="px-4 py-2">
            <Input
              className="h-8 w-32 text-sm"
              value={setdown.SetdownPoint ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...setdowns];
                next[index] = { ...setdown, SetdownPoint: e.target.value };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              type="number"
              className="h-8 w-16 text-sm"
              value={setdown.ArrivalDayNo}
              disabled={disabled}
              onChange={(e) => {
                const next = [...setdowns];
                next[index] = { ...setdown, ArrivalDayNo: Number(e.target.value) || 0 };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              className="h-8 w-24 text-sm"
              value={setdown.ArrivalTime ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...setdowns];
                next[index] = { ...setdown, ArrivalTime: e.target.value };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              type="number"
              className="h-8 w-16 text-sm"
              value={setdown.SetdownDayNo}
              disabled={disabled}
              onChange={(e) => {
                const next = [...setdowns];
                next[index] = { ...setdown, SetdownDayNo: Number(e.target.value) || 0 };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              className="h-8 w-24 text-sm"
              value={setdown.SetdownTime ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...setdowns];
                next[index] = { ...setdown, SetdownTime: e.target.value };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              className="h-8 w-40 text-sm"
              value={setdown.Instructions ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...setdowns];
                next[index] = { ...setdown, Instructions: e.target.value };
                onChange(next);
              }}
            />
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

function EditableFacilities({
  facilities,
  targetBuId,
  onChange,
  disabled,
}: {
  facilities: ContractFacility[];
  targetBuId: string;
  onChange: (facilities: ContractFacility[]) => void;
  disabled?: boolean;
}) {
  if (facilities.length === 0) return null;
  return (
    <DataTable headers={[{ label: <FieldLabel label="Facility" mapped /> }]}>
      {facilities.map((facility, index) => (
        <tr key={index} className="border-b last:border-b-0">
          <td className="px-4 py-2">
            <MasterDataSelect
              targetBuId={targetBuId}
              entityType="facility"
              value={facility.Facility}
              disabled={disabled}
              className="w-48"
              onChange={(v) => {
                const next = [...facilities];
                next[index] = { ...facility, Facility: v };
                onChange(next);
              }}
            />
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

function EditableIncludedItems({
  items,
  targetBuId,
  onChange,
  disabled,
}: {
  items: ContractIncludedItem[];
  targetBuId: string;
  onChange: (items: ContractIncludedItem[]) => void;
  disabled?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <DataTable
      headers={[
        { label: <FieldLabel label="Item" mapped /> },
        { label: <FieldLabel label="Included" required /> },
        { label: <FieldLabel label="Excluded" required /> },
      ]}
    >
      {items.map((item, index) => (
        <tr key={index} className="border-b last:border-b-0">
          <td className="px-4 py-2">
            <MasterDataSelect
              targetBuId={targetBuId}
              entityType="included_item"
              value={item.Item}
              disabled={disabled}
              className="w-48"
              onChange={(v) => {
                const next = [...items];
                next[index] = { ...item, Item: v };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            {item.Included && <Badge variant="success">Included</Badge>}
          </td>
          <td className="px-4 py-2">
            {item.Excluded && <Badge variant="destructive">Excluded</Badge>}
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

function EditableMovementData({
  rows,
  targetBuId,
  onChange,
  disabled,
}: {
  rows: ContractMovementData[];
  targetBuId: string;
  onChange: (rows: ContractMovementData[]) => void;
  disabled?: boolean;
}) {
  if (rows.length === 0) return null;
  return (
    <DataTable
      headers={[
        { label: <FieldLabel label="Title #" required mapped /> },
        { label: "Text" },
        { label: <FieldLabel label="Number" required /> },
        { label: <FieldLabel label="Money" required /> },
        { label: "Date" },
        { label: <FieldLabel label="Yes/No" required /> },
      ]}
    >
      {rows.map((row, index) => (
        <tr key={index} className="border-b last:border-b-0">
          <td className="px-4 py-2">
            <NumericMasterDataSelect
              targetBuId={targetBuId}
              entityType="contract_movement_data_title"
              value={row.ContractMovementDataTitleSerialNo}
              disabled={disabled}
              className="w-40"
              onChange={(v) => {
                const next = [...rows];
                next[index] = { ...row, ContractMovementDataTitleSerialNo: v };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              className="h-8 w-32 text-sm"
              value={row.TextValue ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...rows];
                next[index] = { ...row, TextValue: e.target.value };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              type="number"
              className="h-8 w-24 text-sm"
              value={row.NumberValue}
              disabled={disabled}
              onChange={(e) => {
                const next = [...rows];
                next[index] = { ...row, NumberValue: Number(e.target.value) || 0 };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              type="number"
              className="h-8 w-24 text-sm"
              value={row.MoneyValue}
              disabled={disabled}
              onChange={(e) => {
                const next = [...rows];
                next[index] = { ...row, MoneyValue: Number(e.target.value) || 0 };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Input
              className="h-8 w-32 text-sm"
              value={row.DateValue ?? ""}
              disabled={disabled}
              onChange={(e) => {
                const next = [...rows];
                next[index] = { ...row, DateValue: e.target.value };
                onChange(next);
              }}
            />
          </td>
          <td className="px-4 py-2">
            <Badge variant={row.YesNoValue ? "success" : "outline"}>
              {row.YesNoValue ? "Yes" : "No"}
            </Badge>
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

function EditableMovementCoreFields({
  movement,
  targetBuId,
  onChange,
  disabled,
}: {
  movement: ContractMovement;
  targetBuId: string;
  onChange: (movement: ContractMovement) => void;
  disabled?: boolean;
}) {
  const textField = (label: string, key: keyof ContractMovement) => (
    <TextField
      key={key}
      label={label}
      value={movement[key] as string | undefined}
      disabled={disabled}
      onChange={(value) => onChange({ ...movement, [key]: value })}
    />
  );
  const numberField = (label: string, key: keyof ContractMovement, required?: boolean) => (
    <NumberField
      key={key}
      label={label}
      required={required}
      value={(movement[key] as number | undefined) ?? 0}
      disabled={disabled}
      onChange={(value) => onChange({ ...movement, [key]: value })}
    />
  );

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
      {textField("Movement Description", "MovementDescription")}
      <NumericMasterDataSelect
        label="Start Base Serial No"
        required
        targetBuId={targetBuId}
        entityType="base"
        value={movement.StartBaseSerialNo}
        disabled={disabled}
        onChange={(v) => onChange({ ...movement, StartBaseSerialNo: v })}
      />
      {textField("Start Time", "StartTime")}
      {numberField("Position Day", "PositionDay", true)}
      {textField("Position Time", "PositionTime")}
      {numberField("Pickup Day", "PickupDay", true)}
      {textField("Pickup Time", "PickupTime")}
      {textField("Pickup", "Pickup")}
      {textField("Pickup Instructions", "PickupInstructions")}
      {textField("Destination", "Destination")}
      {textField("Destination Instructions", "DestinationInstructions")}
      {numberField("Arrival Day", "ArrivalDay", true)}
      {textField("Arrival Time", "ArrivalTime")}
      {numberField("Leave Day", "LeaveDay", true)}
      {textField("Leave Time", "LeaveTime")}
      {numberField("Back Day", "BackDay", true)}
      {textField("Back Time", "BackTime")}
      {numberField("Finish Day", "FinishDay", true)}
      {textField("Finish Time", "FinishTime")}
      <NumericMasterDataSelect
        label="Finish Base Serial No"
        required
        targetBuId={targetBuId}
        entityType="base"
        value={movement.FinishBaseSerialNo}
        disabled={disabled}
        onChange={(v) => onChange({ ...movement, FinishBaseSerialNo: v })}
      />
      {numberField("Passengers", "Passengers", true)}
      {textField("Client Reference 1", "ClientReference1")}
      {textField("Client Reference 2", "ClientReference2")}
      {textField("Route", "Route")}
      {textField("Further Requirements", "FurtherRequirements")}
      {textField("Notes", "Notes")}
    </div>
  );
}

/**
 * A movement's editable nested collections — vehicles/drivers, pickups,
 * setdowns, facilities, included items, movement data — as pill tab toggles,
 * mirroring the read-only `MovementsTable`'s `MovementNestedTables`. Only
 * collections with data get a tab. Rendered only while its row's accordion
 * is open, so a fresh mount (fresh tab state) happens naturally every time a
 * different movement is expanded — no key trick needed.
 */
function EditableMovementNestedTabs({
  movement,
  targetBuId,
  disabled,
  onChange,
}: {
  movement: ContractMovement;
  targetBuId: string;
  disabled?: boolean;
  onChange: (movement: ContractMovement) => void;
}) {
  const categories = [
    {
      value: "vehicles",
      label: "Vehicles",
      count: movement.ContractVehicles?.length ?? 0,
      render: () => (
        <EditableVehicles
          vehicles={movement.ContractVehicles ?? []}
          targetBuId={targetBuId}
          disabled={disabled}
          onChange={(vehicles) => onChange({ ...movement, ContractVehicles: vehicles })}
        />
      ),
    },
    {
      value: "pickups",
      label: "Pickups",
      count: movement.ContractPickups?.length ?? 0,
      render: () => (
        <EditablePickups
          pickups={movement.ContractPickups ?? []}
          disabled={disabled}
          onChange={(pickups) => onChange({ ...movement, ContractPickups: pickups })}
        />
      ),
    },
    {
      value: "setdowns",
      label: "Setdowns",
      count: movement.ContractSetdowns?.length ?? 0,
      render: () => (
        <EditableSetdowns
          setdowns={movement.ContractSetdowns ?? []}
          disabled={disabled}
          onChange={(setdowns) => onChange({ ...movement, ContractSetdowns: setdowns })}
        />
      ),
    },
    {
      value: "facilities",
      label: "Facilities",
      count: movement.ContractFacilities?.length ?? 0,
      render: () => (
        <EditableFacilities
          facilities={movement.ContractFacilities ?? []}
          targetBuId={targetBuId}
          disabled={disabled}
          onChange={(facilities) => onChange({ ...movement, ContractFacilities: facilities })}
        />
      ),
    },
    {
      value: "includedItems",
      label: "Included Items",
      count: movement.ContractIncludedItems?.length ?? 0,
      render: () => (
        <EditableIncludedItems
          items={movement.ContractIncludedItems ?? []}
          targetBuId={targetBuId}
          disabled={disabled}
          onChange={(items) => onChange({ ...movement, ContractIncludedItems: items })}
        />
      ),
    },
    {
      value: "movementData",
      label: "Movement Data",
      count: movement.ContractMovementData?.length ?? 0,
      render: () => (
        <EditableMovementData
          rows={movement.ContractMovementData ?? []}
          targetBuId={targetBuId}
          disabled={disabled}
          onChange={(rows) => onChange({ ...movement, ContractMovementData: rows })}
        />
      ),
    },
  ].filter((category) => category.count > 0);

  if (categories.length === 0) {
    return <p className="text-sm text-muted-foreground">This movement has no nested data.</p>;
  }

  return (
    <Tabs defaultValue={categories[0]?.value}>
      <TabsList>
        {categories.map((category) => (
          <TabsTrigger key={category.value} value={category.value}>
            {category.label} ({category.count})
          </TabsTrigger>
        ))}
      </TabsList>
      {categories.map((category) => (
        <TabsContent key={category.value} value={category.value}>
          {category.render()}
        </TabsContent>
      ))}
    </Tabs>
  );
}

/**
 * Full-width editable movements table — mirrors `MovementsTable`'s
 * accordion layout (click a row to expand its edit form + nested-collection
 * tabs inline, only one open at a time), but every row is an input, and
 * every master/reference-data column (VehicleType, Facility, Item, the
 * Base/DataTitle serial numbers) is a `MasterDataSelect` sourced from the
 * target BU. `Cancelled`/`SingleJourney`/`VehicleToStay` stay read-only
 * badges — not reference data, low edit value.
 */
export function EditableMovements({
  movements,
  targetBuId,
  onChange,
  disabled,
}: EditableMovementsProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    movements.length === 1 ? 0 : null,
  );

  function updateMovement(index: number, next: ContractMovement) {
    const copy = [...movements];
    copy[index] = next;
    onChange(copy);
  }

  function handleToggle(index: number) {
    setExpandedIndex((current) => (current === index ? null : index));
  }

  return (
    <div className="space-y-2">
      <h4 className="font-display text-sm font-semibold">Movements ({movements.length})</h4>
      <p className="text-xs text-muted-foreground">
        Select a movement to edit its detail, vehicles, pickups and setdowns.
      </p>
      <DataTable
        headers={[
          { label: "", className: "w-8" },
          { label: "Movement" },
          { label: "Pickup Time" },
          { label: "Passengers" },
          { label: "Vehicles" },
          { label: "Pickups" },
          { label: "Setdowns" },
          { label: "Facilities" },
          { label: "Status" },
        ]}
        empty={<span className="text-sm text-muted-foreground">No movements.</span>}
      >
        {movements.map((movement, index) => {
          const isExpanded = expandedIndex === index;
          return (
            <Fragment key={index}>
              <tr
                className={cn(
                  "cursor-pointer border-b last:border-b-0 hover:bg-muted/40",
                  isExpanded && "bg-[var(--bg-accent)]",
                )}
                onClick={() => handleToggle(index)}
              >
                <td className="px-2 py-2">
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 text-muted-foreground transition-transform",
                      isExpanded && "rotate-90",
                    )}
                  />
                </td>
                <td className="px-4 py-2">{movementSummary(movement)}</td>
                <td className="px-4 py-2">{formatCellValue(movement.PickupTime)}</td>
                <td className="px-4 py-2">{movement.Passengers}</td>
                <td className="px-4 py-2">{movement.ContractVehicles?.length || "—"}</td>
                <td className="px-4 py-2">{movement.ContractPickups?.length || "—"}</td>
                <td className="px-4 py-2">{movement.ContractSetdowns?.length || "—"}</td>
                <td className="px-4 py-2">{movement.ContractFacilities?.length || "—"}</td>
                <td className="px-4 py-2">
                  {movement.Cancelled ? (
                    <Badge variant="destructive">Cancelled</Badge>
                  ) : (
                    <Badge variant="success">Active</Badge>
                  )}
                </td>
              </tr>
              {isExpanded && (
                <tr className="border-b bg-[var(--bg-2)] last:border-b-0">
                  <td colSpan={9} className="px-4 py-3">
                    <div className="space-y-4">
                      <EditableMovementCoreFields
                        movement={movement}
                        targetBuId={targetBuId}
                        disabled={disabled}
                        onChange={(next) => updateMovement(index, next)}
                      />
                      <EditableMovementNestedTabs
                        movement={movement}
                        targetBuId={targetBuId}
                        disabled={disabled}
                        onChange={(next) => updateMovement(index, next)}
                      />
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </DataTable>
    </div>
  );
}
